// pages/ChangePinPage.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";
import PinInput from "../../components/feature/PinInput";

export default function ChangePinPage() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [step,       setStep]       = useState(1);  // 1=current, 2=new, 3=confirm
  const [current,    setCurrent]    = useState("");
  const [newPin,     setNewPin]     = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error,      setError]      = useState("");
  const [loading,    setLoading]    = useState(false);

  const next = (val, setter, nextStep) => {
    setError("");
    setter(val);
    if (val.length === 4) setTimeout(() => setStep(nextStep), 200);
  };

  const handleConfirmChange = (val) => {
    setError("");
    setConfirmPin(val);
    if (val.length === 4) submit(val);
  };

  const submit = async (confirmVal) => {
    const c = confirmVal || confirmPin;
    if (c !== newPin) {
      setError("New PINs do not match.");
      setConfirmPin("");
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post("/mtrade/user/transaction-pin/change", {
        currentPin: current,
        newPin,
        confirmNewPin: c,
      });
      if (res.data.status) {
        updateUser({ hasTransactionPin: true });
        toast.success("PIN changed successfully!");
        navigate(-1);
      } else {
        setError(res.data.message || "Failed to change PIN");
        if (res.data.message?.includes("incorrect")) {
          // Wrong current PIN — restart from step 1
          setCurrent(""); setNewPin(""); setConfirmPin(""); setStep(1);
        } else {
          setConfirmPin("");
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to change PIN";
      setError(msg);
      if (msg.includes("incorrect") || msg.includes("locked")) {
        setCurrent(""); setNewPin(""); setConfirmPin(""); setStep(1);
      } else {
        setConfirmPin("");
      }
    } finally {
      setLoading(false);
    }
  };

  const STEPS = [
    { label: "Enter current PIN",    hint: "Your existing transaction PIN" },
    { label: "Enter new PIN",        hint: "Choose a new 4-digit PIN" },
    { label: "Confirm new PIN",      hint: "Re-enter your new PIN" },
  ];

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="bg-surface px-5 pt-12 pb-5 border-b border-border">
        <div className="flex items-center gap-3">
          <button
            onClick={() => step > 1 ? setStep(s => s - 1) : navigate(-1)}
            className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center"
          >
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <div>
            <h1 className="text-primary font-bold text-lg font-display">
              Change PIN
            </h1>
            <p className="text-muted text-xs">{STEPS[step - 1].hint}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-8 py-10">
        <div className="flex gap-2 mb-10">
          {[1,2,3].map(s => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${
              s <= step ? "w-8 bg-brand" : "w-4 bg-border-strong"
            }`} />
          ))}
        </div>

        {step === 1 && (
          <PinInput value={current} onChange={v => next(v, setCurrent, 2)}
            label={STEPS[0].label} error={error} disabled={loading} />
        )}
        {step === 2 && (
          <PinInput value={newPin} onChange={v => next(v, setNewPin, 3)}
            label={STEPS[1].label} error={error} disabled={loading} />
        )}
        {step === 3 && (
          <PinInput value={confirmPin} onChange={handleConfirmChange}
            label={STEPS[2].label} error={error} disabled={loading} />
        )}

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-brand">
            <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand-light)", borderTopColor: "var(--color-brand)" }} />
            <span className="text-sm font-medium">Updating PIN…</span>
          </div>
        )}
      </div>
    </div>
  );
}
